import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthContext'
import { supabase } from '../../../config/supabase'
import FormInput from '../../styled-components/FormElements/FormInput/FormInput'
import MegaCard, { MegaCardBody, MegaCardFooter, MegaCardHead } from '../../styled-components/Card/MegaCard'
import FormAlertDanger from '../../styled-components/FormElements/Alerts/FormAlert'
import MegaButton from '../../styled-components/Buttons/MegaButton/MegaButton'
import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri'

export default function Login() {

  const [ email, set_email ] = useState('')
  const [ password, set_password ] = useState('')
  const [ rememberMe, set_rememberMe ] = useState(false)
  const [ errorMessage, set_errorMessage ] = useState(null)
  const [ showPassword, setShowPassword ] = useState(false)
  const [ isValidatingAdmin, setIsValidatingAdmin ] = useState(false)

  // Hook de autenticación
  const { login, isLoggingIn, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Efecto para cargar preferencias guardadas al montar el componente
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberMePreference = localStorage.getItem('rememberMe') === 'true';
    
    if (rememberedEmail) {
      set_email(rememberedEmail);
      set_rememberMe(rememberMePreference);
    } else {
      set_rememberMe(rememberMePreference);
    }
  }, []);

  // Efecto para manejar redirección después del login exitoso
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Maneja el evento de presionar Enter en los inputs del formulario.
  // Ejecuta la función de login si ambos campos están completos.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && email && password.length >= 6) {
      iniciarSession()
    }
  }

  // Valida si el email tiene rol de administrador antes de proceder con el login.
  // Evita crear sesiones temporales para usuarios no administradores.
  const validateAdminBeforeLogin = async () => {
    try {
      const { data: isAdminData, error: adminError } = await supabase.rpc('check_email_is_admin', {
        user_email: email
      });

      if (adminError) {
        throw new Error('Error al verificar permisos de administrador');
      }

      if (!isAdminData) {
        throw new Error('No tienes permisos de administrador para acceder al panel de control');
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  async function iniciarSession(){
    set_errorMessage(null)
    setIsValidatingAdmin(true)
    
    try {
      console.log(' --- Validando si el usuario es admin antes del login')
      // Primero valida si el email tiene rol admin
      await validateAdminBeforeLogin()
      
      console.log(' --- Usuario es admin, procediendo con login')
      // Si es admin, procede con el login normal
      await login(email, password, rememberMe)
    } catch (error) {
      set_errorMessage(error)
    } finally {
      setIsValidatingAdmin(false)
    }
  }

  return (
    <article className='bg-mygray-900 text-white flex items-center justify-center min-h-screen p-4'>
      <MegaCard otraClassname='w-full max-w-md'>
        <MegaCardHead>
          <h3>Panelcito de Control</h3>
        </MegaCardHead>
        <MegaCardBody>
          <FormInput
            labelText={'Email'}
            type="text"
            value={email}
            onChange={e => set_email(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <div className='mb-4 relative'>
            <FormInput
              labelText={'Contraseña'}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => set_password(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute right-3 top-10 hover:text-lila transition-colors'
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <RiEyeOffLine size={20} /> : <RiEyeLine size={20} />}
            </button>
          </div>

          <div className="flex items-center mt-4">
            <input
              id="remember-me"
              name="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={e => set_rememberMe(e.target.checked)}
              className="h-4 w-4 text-lila focus:ring-lila border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm">
              Recordarme
            </label>
          </div>

          {errorMessage && <FormAlertDanger labelText={errorMessage.message} onClick={()=> set_errorMessage(null)}/>}

        </MegaCardBody>

        <MegaCardFooter>
          <section className='flex justify-end gap-4'>
            <div></div>
            <MegaButton
              loading={isLoggingIn || isValidatingAdmin}
              labelText={isValidatingAdmin ? 'Verificando permisos...' : 'Ingresar'}
              className={(isLoggingIn || isValidatingAdmin) ? 'bg-primary-500 text-white' : 'bg-primary-500 text-white px-4 transition-all duration-300'} 
              onClick={()=> iniciarSession()}
              disabled={!email || password.length < 6 || isValidatingAdmin}
            />
          </section>
        </MegaCardFooter>

      </MegaCard>
    </article>
  )
}
